import React, {type ReactNode, useId} from 'react'
import {CheckCircleIcon} from '@heroicons/react/20/solid'
import classNames from '@/Utils/ClassNames'
import {type EventRegistrationPaymentStatus} from '@/Types/EventRegistrationEntry'

export interface TournamentClassificationOption {
    classification_id: string
    name: string
    position: number
}

export interface TournamentAgeOption {
    age_id: string
    name: string
    position: number
    entry_fee: number | null
    registered_count: number
    capacity: number | null
    classifications: TournamentClassificationOption[]
}

export interface Tournament {
    id: string
    name: string
    starts_at: string
    ends_at: string | null
    registered_count: number
    capacity: number | null
    entry_fee: number | null
    payment_status: EventRegistrationPaymentStatus | null
    payment_due_at: string | null
    default_age_id: string | null
    available_ages: TournamentAgeOption[]
}

export type TournamentPick = {
    ageId: string
    classificationId: string | null
}

/**
 * The registered-count caption rendered beneath a tournament's name
 * (ENG-4061). A division with no `max_teams` cap has no denominator to
 * report, so it drops the "of x" rather than inventing one.
 */
export function formatRegisteredCount(tournament: Tournament): string {
    return tournament.capacity == null
        ? `${tournament.registered_count} registered`
        : `${tournament.registered_count} of ${tournament.capacity} registered`
}

export function isAlreadyPaid(tournament: Tournament): boolean {
    return tournament.payment_status === 'paid' || tournament.payment_status === 'paid_offline'
}

export function isAlreadyRegistered(tournament: Tournament): boolean {
    return tournament.payment_status != null
}

/**
 * An entry the coach still owes for. Clicking that tournament on the public
 * form asks "Looking to pay?" instead of selecting it again (ENG-4346).
 * Paid and paid-offline stay locked; a refund can still be selected.
 */
export function isUnpaidRegistration(tournament: Tournament): boolean {
    return tournament.payment_status === 'not_paid'
}

/**
 * The age id currently selected for $tournament — the coach's explicit
 * pick if any, otherwise the auto-resolved default. `||`, not `??`: an
 * empty string counts as "nothing chosen" alongside null/undefined.
 */
export function resolvedAgeId(tournament: Tournament | undefined, pick?: TournamentPick | null): string | null {
    return tournament == null ? null : pick?.ageId || tournament.default_age_id || null
}

/**
 * The classification id to auto-select for $ageId, when it offers exactly
 * one — HS age levels (15U-18U) typically offer only "Open" (ENG-4260).
 * Returns null for zero or multiple options, leaving the coach to pick one
 * via ClassificationCardGroup.
 */
function soleClassificationId(tournament: Tournament, ageId: string): string | null {
    const classifications = tournament.available_ages.find((option) => option.age_id === ageId)?.classifications ?? []

    return classifications.length === 1 ? classifications[0].classification_id : null
}

/**
 * The classification id currently selected for $ageId within $tournament —
 * the coach's explicit pick if any, otherwise $ageId's sole classification
 * when it only offers one (ENG-4260). Mirrors resolvedAgeId: resolved at
 * read time rather than eagerly written into `picks` state, so every place
 * that reads a pick's classification agrees on the same value.
 */
export function resolvedClassificationId(tournament: Tournament | undefined, ageId: string | null, pick?: TournamentPick | null): string | null {
    if (pick?.classificationId != null) {
        return pick.classificationId
    }

    return tournament == null || ageId == null ? null : soleClassificationId(tournament, ageId)
}

/**
 * The display name for resolvedClassificationId()'s result — an auto-select
 * (ENG-4260) changes what a tournament's classification resolves to with no
 * click of its own to self-announce, so callers use this to fold the result
 * into an existing live region rather than leaving the change silent for
 * screen reader users.
 */
export function resolvedClassificationName(tournament: Tournament | undefined, ageId: string | null, pick?: TournamentPick | null): string | null {
    const classificationId = resolvedClassificationId(tournament, ageId, pick)

    if (classificationId == null || tournament == null || ageId == null) {
        return null
    }

    return tournament.available_ages
        .find((option) => option.age_id === ageId)
        ?.classifications.find((classification) => classification.classification_id === classificationId)?.name ?? null
}

/**
 * "Small cards" single-select button group for a tournament's play-up age
 * or classification options — plain click-activated buttons (Tab per button)
 * with `aria-pressed` inside a `role="group"`, not Headless UI's `RadioGroup`.
 * Matches this codebase's established pattern for single-select toggle
 * groups (see the quick-filter buttons in Events/Tabs/AgentsTab.tsx) —
 * `radiogroup`'s arrow-key roving focus doesn't fit a set of independently
 * tabbable buttons. Selected-card styling mirrors Components/Forms/ToggleCard.tsx.
 *
 * `readOnly` renders $options as static, non-interactive pills instead of
 * buttons — for a division with nothing left to pick (ENG-4260's sole
 * classification), rather than a fragile second copy of the pill styling.
 * The selected pill still gets a checkmark, since "nothing to click" is
 * exactly when a coach most needs the confirmation that something *was*
 * chosen for them.
 */
function DivisionCardGroup(
    {label, caption, testId, options, selectedId, disabled, readOnly = false, showSelectedCheckmark = false, onChange}: {
        label: string
        caption: string
        testId: string
        options: {id: string, name: string}[]
        selectedId: string
        disabled: boolean
        readOnly?: boolean
        showSelectedCheckmark?: boolean
        onChange: (id: string) => void
    },
): ReactNode {
    const labelId = useId()

    // No left indent: this group now renders inside the tournament card's own
    // `px-4` band behind a full-bleed divider, which already establishes the
    // hierarchy the old `ml-6` (checkbox width + gap) was communicating. Keeping
    // it would leave the pills 40px from the card's left edge but 16px from its
    // right, and eat 13% of the text column at 320px.
    return <div>
        <p id={labelId} className={'form-control-label'}>
            {caption}
            <span className={'sr-only'}>{` for ${label}`}</span>
        </p>
        <div
            role={'group'}
            aria-labelledby={labelId}
            data-testid={testId}
            className={'flex flex-wrap gap-2'}
        >
            {options.map((option) => {
                const isSelected = option.id === selectedId
                // Same tokens as the enclosing tournament card. The pills sit
                // *on* that card now, so the old royal-40/storm-80 borders
                // were 1.7:1 and 1.4:1 against its royal-60 fill, and
                // `bg-royal-60/5` resolved to exactly the card color — the
                // selected and unselected fills were indistinguishable. White
                // fills read as "on top of" the card and both borders clear 3:1.
                const pillClasses = classNames(
                    'inline-flex min-h-10 max-w-full items-center gap-1.5 break-words rounded-lg border px-4 py-2 text-[14px] font-medium',
                    isSelected
                        ? 'border-royal-20 bg-white text-royal-10'
                        : 'border-storm-50 bg-white text-gray-700',
                )

                if (readOnly) {
                    return (
                        <span
                            key={option.id}
                            aria-label={`${option.name}, ${caption} for ${label}, selected automatically`}
                            className={pillClasses}
                        >
                            {isSelected && <CheckCircleIcon aria-hidden={'true'} className={'size-4 shrink-0 text-shamrock-50'}/>}
                            {option.name}
                        </span>
                    )
                }

                return (
                    <button
                        key={option.id}
                        type={'button'}
                        disabled={disabled}
                        aria-pressed={isSelected}
                        aria-label={`${option.name}, ${caption} for ${label}`}
                        onClick={() => onChange(option.id)}
                        className={classNames(
                            pillClasses,
                            'scroll-mt-28 transition-colors',
                            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20 focus-visible:ring-offset-2',
                            isSelected ? '' : 'hover:bg-storm-90',
                            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                        )}
                    >
                        {showSelectedCheckmark && isSelected && <CheckCircleIcon aria-hidden={'true'} className={'size-4 shrink-0 text-shamrock-50'}/>}
                        {option.name}
                    </button>
                )
            })}
        </div>
    </div>
}

export function AgeGroupCardGroup(
    {tournament, selectedAgeId, disabled, onChange}: {
        tournament: Tournament
        selectedAgeId: string
        disabled: boolean
        onChange: (ageId: string) => void
    },
): ReactNode {
    const options = [...tournament.available_ages]
        .sort((a, b) => a.position - b.position)
        .map((option) => ({id: option.age_id, name: option.name}))

    return <DivisionCardGroup
        label={tournament.name}
        caption={'Age group'}
        testId={`registration-tournament-age-${tournament.id}`}
        options={options}
        selectedId={selectedAgeId}
        disabled={disabled}
        showSelectedCheckmark
        onChange={onChange}
    />
}

export function ClassificationCardGroup(
    {tournament, selectedAgeId, selectedClassificationId, disabled, onChange}: {
        tournament: Tournament
        selectedAgeId: string
        selectedClassificationId: string
        disabled: boolean
        onChange: (classificationId: string) => void
    },
): ReactNode {
    const ageOption = tournament.available_ages.find((option) => option.age_id === selectedAgeId)
    const options = [...(ageOption?.classifications ?? [])]
        .sort((a, b) => a.position - b.position)
        .map((option) => ({id: option.classification_id, name: option.name}))

    if (options.length === 0) {
        return null
    }

    // A sole classification auto-selects (ENG-4260, soleClassificationId)
    // rather than making the coach click through a single-option group — but
    // it's still surfaced read-only, not silently hidden, so the coach can
    // see what they're registering under.
    if (options.length === 1) {
        return <DivisionCardGroup
            label={tournament.name}
            caption={'Classification'}
            testId={`registration-tournament-classification-auto-${tournament.id}`}
            options={options}
            selectedId={options[0].id}
            disabled={false}
            readOnly
            onChange={() => {}}
        />
    }

    return <DivisionCardGroup
        label={tournament.name}
        caption={'Classification'}
        testId={`registration-tournament-classification-${tournament.id}`}
        options={options}
        selectedId={selectedClassificationId}
        disabled={disabled}
        // A manual pick here gets the same checkmark the sole-classification
        // auto-select above already shows, so a coach reads "chosen" the
        // same way whether the app resolved it or they clicked it (ENG-4262).
        showSelectedCheckmark
        onChange={onChange}
    />
}
